import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getQuoteTemplates, updateQuoteTemplate } from '../api';
import { Save, RotateCcw, Palette, Layout, Eye, Type, Sliders, FileText, Plus, Trash2, GripVertical, Layers } from 'lucide-react';

const DEFAULT_TEMPLATE_CONFIG = {
  colors: { primary: '#1a2744', accent: '#3b82f6', accentLight: '#eff6ff' },
  headerText: 'Quotation',
  footerText: 'Confidential & Proprietary',
  showTagline: true,
  showPageNumbers: true,
  sections: {
    showCustomerInfo: true,
    showTerms: true,
    showSalesRep: true,
    showValidity: true,
    showSpecialRequirements: true,
    showLeadTime: true,
  },
  spacing: {
    sectionSpacing: 12,
    fieldSpacing: 10,
    compactMode: false,
  },
  defaultTerms: 'All prices are valid for the period specified above. Payment terms are Net 30 unless otherwise agreed. All sales are subject to our standard terms and conditions.',
  customSections: [],
  sectionOrder: ['termsAndConditions', 'salesRepresentative'],
};

// --- Sub-components ---

const Toggle = ({ checked, onChange, label }) => (
  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', padding: '8px 0' }}>
    <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>{label}</span>
    <div
      onClick={(e) => { e.preventDefault(); onChange(!checked); }}
      style={{
        width: '40px', height: '22px', borderRadius: '11px',
        background: checked ? 'var(--accent-primary)' : 'var(--border-color)',
        position: 'relative', transition: 'background 0.2s', cursor: 'pointer',
        flexShrink: 0,
      }}
    >
      <div style={{
        width: '18px', height: '18px', borderRadius: '50%', background: '#fff',
        position: 'absolute', top: '2px', left: checked ? '20px' : '2px',
        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </div>
  </label>
);

const ColorPicker = ({ label, value, onChange }) => (
  <div className="form-group" style={{ marginBottom: '12px' }}>
    <label className="form-label">{label}</label>
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: '40px', height: '36px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', padding: '2px' }}
      />
      <input
        type="text"
        className="form-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ fontFamily: 'monospace', fontSize: '13px' }}
      />
    </div>
  </div>
);

const SectionCard = ({ icon: Icon, title, children, action }) => (
  <div className="card" style={{ marginBottom: '16px' }}>
    <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      {Icon && <Icon size={18} style={{ color: 'var(--accent-primary)' }} />}
      <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, flex: 1 }}>{title}</h3>
      {action}
    </div>
    <div className="card-body">
      {children}
    </div>
  </div>
);

const RangeSlider = ({ label, value, onChange, min, max, step }) => (
  <div className="form-group" style={{ marginBottom: '14px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
      <label className="form-label mb-0">{label}</label>
      <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--accent-primary)', background: 'var(--bg-tertiary)', padding: '2px 8px', borderRadius: 'var(--radius-sm)' }}>{value}px</span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(parseInt(e.target.value))}
      style={{ width: '100%', accentColor: 'var(--accent-primary)' }}
    />
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
      <span>{min}px</span>
      <span>{max}px</span>
    </div>
  </div>
);

// --- Section Order helpers ---

const BUILT_IN_SECTIONS = {
  termsAndConditions: 'Terms & Conditions',
  salesRepresentative: 'Sales Representative',
};

function getSectionLabel(entry, customSections) {
  if (BUILT_IN_SECTIONS[entry]) return BUILT_IN_SECTIONS[entry];
  if (entry.startsWith('customSection:')) {
    const csId = entry.replace('customSection:', '');
    const cs = customSections.find((s) => s.id === csId);
    return cs ? cs.title || 'Untitled Section' : 'Deleted Section';
  }
  return entry;
}


// --- Live Preview ---

const LivePreview = ({ config }) => {
  // Build preview sections in sectionOrder
  const renderPreviewSection = (entry, idx) => {
    const sectionStyle = { marginBottom: `${config.spacing.sectionSpacing}px` };
    const titleStyle = { fontSize: '9px', fontWeight: 700, color: config.colors.primary, borderBottom: `2px solid ${config.colors.accent}`, paddingBottom: '2px', marginBottom: '6px', display: 'inline-block' };
    const calloutStyle = { background: config.colors.accentLight, borderLeft: `3px solid ${config.colors.accent}`, padding: '6px 10px', borderRadius: '0 4px 4px 0', fontSize: '7px', color: '#333' };

    if (entry === 'termsAndConditions' && config.sections.showTerms) {
      return (
        <div key={entry + idx} style={sectionStyle}>
          <div style={titleStyle}>Terms & Conditions</div>
          <div style={calloutStyle}>
            {config.defaultTerms ? config.defaultTerms.substring(0, 80) + '...' : 'Standard terms will appear here...'}
          </div>
        </div>
      );
    }
    if (entry === 'salesRepresentative' && config.sections.showSalesRep) {
      return (
        <div key={entry + idx} style={sectionStyle}>
          <div style={titleStyle}>Sales Representative</div>
          <div style={{ fontSize: '7px', color: '#666' }}>John Smith | john@company.com | (510) 555-0100</div>
        </div>
      );
    }
    if (entry.startsWith('customSection:')) {
      const csId = entry.replace('customSection:', '');
      const cs = config.customSections.find((s) => s.id === csId);
      if (!cs || !cs.enabled) return null;
      return (
        <div key={entry + idx} style={sectionStyle}>
          <div style={titleStyle}>{cs.title || 'Untitled'}</div>
          <div style={calloutStyle}>
            {cs.content ? cs.content.substring(0, 80) + '...' : 'Section content...'}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{
      background: '#fff',
      border: '1px solid var(--border-color)',
      borderRadius: '8px',
      padding: '0',
      overflow: 'hidden',
      boxShadow: 'var(--shadow-md)',
      fontSize: '8px',
      minHeight: '500px',
      position: 'sticky',
      top: '20px',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{ background: config.colors.primary, padding: '12px 16px', color: '#fff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '9px', opacity: 0.7 }}>LOGO</span>
          <span style={{ fontSize: '14px', fontWeight: 700 }}>{config.headerText}</span>
        </div>
        {config.showTagline && (
          <div style={{ textAlign: 'center', fontSize: '7px', opacity: 0.6, marginTop: '4px', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '4px' }}>
            Light-Speed Connectivity | 47257 Fremont Blvd, Fremont, CA 94538
          </div>
        )}
      </div>

      {/* Meta bar */}
      <div style={{ background: '#f8fafc', padding: '6px 16px', display: 'flex', gap: '16px', borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
        <div>
          <span style={{ fontSize: '6px', color: '#6b7280', display: 'block' }}>Quote #</span>
          <span style={{ fontSize: '8px', fontWeight: 600 }}>US-20260223-1</span>
        </div>
        <div>
          <span style={{ fontSize: '6px', color: '#6b7280', display: 'block' }}>Date</span>
          <span style={{ fontSize: '8px', fontWeight: 600 }}>Feb 23, 2026</span>
        </div>
        {config.sections.showValidity && (
          <div>
            <span style={{ fontSize: '6px', color: '#6b7280', display: 'block' }}>Valid Until</span>
            <span style={{ fontSize: '8px', fontWeight: 600 }}>Mar 25, 2026</span>
          </div>
        )}
        <div style={{ marginLeft: 'auto' }}>
          <span style={{ fontSize: '6px', color: '#6b7280', display: 'block' }}>Status</span>
          <span style={{ fontSize: '7px', fontWeight: 600, background: '#dcfce7', color: '#166534', padding: '1px 6px', borderRadius: '3px' }}>Draft</span>
        </div>
      </div>

      {/* Body sections */}
      <div style={{ padding: '12px 16px', flex: 1 }}>
        {/* Customer Info */}
        {config.sections.showCustomerInfo && (
          <div style={{ marginBottom: `${config.spacing.sectionSpacing}px` }}>
            <div style={{ fontSize: '9px', fontWeight: 700, color: config.colors.primary, borderBottom: `2px solid ${config.colors.accent}`, paddingBottom: '2px', marginBottom: '6px', display: 'inline-block' }}>Customer Information</div>
            <div style={{ background: '#f8fafc', borderRadius: '4px', height: '40px' }} />
          </div>
        )}

        {/* Special Requirements */}
        {config.sections.showSpecialRequirements && (
          <div style={{ marginBottom: `${config.spacing.sectionSpacing}px` }}>
            <div style={{ fontSize: '9px', fontWeight: 700, color: config.colors.primary, borderBottom: `2px solid ${config.colors.accent}`, paddingBottom: '2px', marginBottom: '6px', display: 'inline-block' }}>Special Requirements</div>
            <div style={{ background: config.colors.accentLight, borderLeft: `3px solid ${config.colors.accent}`, padding: '6px 10px', borderRadius: '0 4px 4px 0', fontSize: '7px', color: '#333' }}>
              Customer-specific notes appear here...
            </div>
          </div>
        )}

        {/* Line items table mockup */}
        <div style={{ marginBottom: `${config.spacing.sectionSpacing}px` }}>
          <div style={{ fontSize: '9px', fontWeight: 700, color: config.colors.primary, borderBottom: `2px solid ${config.colors.accent}`, paddingBottom: '2px', marginBottom: '6px', display: 'inline-block' }}>Product Line Items</div>
          <div style={{ background: config.colors.primary, borderRadius: '4px 4px 0 0', padding: '4px 8px', color: '#fff', fontSize: '7px', display: 'flex', gap: '8px' }}>
            <span>#</span>
            <span style={{ flex: 1 }}>Description</span>
            <span>Qty</span>
            <span>Price</span>
            <span>Total</span>
            {config.sections.showLeadTime && <span>Lead</span>}
          </div>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderTop: 0, padding: '4px 8px', fontSize: '7px', display: 'flex', gap: '8px' }}>
            <span>1</span>
            <span style={{ flex: 1 }}>Sample Product 1</span>
            <span>10</span>
            <span>$5.00</span>
            <span>$50.00</span>
            {config.sections.showLeadTime && <span>4 wks</span>}
          </div>
          <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderTop: 0, padding: '4px 8px', fontSize: '7px', display: 'flex', gap: '8px' }}>
            <span>2</span>
            <span style={{ flex: 1 }}>Sample Product 2</span>
            <span>5</span>
            <span>$12.00</span>
            <span>$60.00</span>
            {config.sections.showLeadTime && <span>6 wks</span>}
          </div>
          <div style={{ borderTop: '2px solid #e5e7eb', padding: '4px 8px', fontSize: '8px', fontWeight: 700, textAlign: 'right', color: config.colors.primary }}>
            Total: $110.00
          </div>
        </div>

        {/* Reorderable sections — rendered in sectionOrder */}
        {config.sectionOrder.map((entry, idx) => renderPreviewSection(entry, idx))}
      </div>

      {/* Footer */}
      <div style={{ background: config.colors.primary, padding: '6px 16px', color: '#c0c8d8', fontSize: '6px', display: 'flex', justifyContent: 'space-between', marginTop: 'auto' }}>
        <span>ActiveOptics</span>
        {config.showPageNumbers && <span>Page 1 of 1</span>}
        <span>{config.footerText}</span>
      </div>
    </div>
  );
};


// --- Main Component ---

function QuoteTemplateEditor() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [config, setConfig] = useState(DEFAULT_TEMPLATE_CONFIG);
  const [templateId, setTemplateId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Drag refs for section ordering
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  useEffect(() => {
    loadTemplate();
  }, []);

  // Auto-dismiss messages after 4 seconds
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => setMessage({ type: '', text: '' }), 4000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const res = await getQuoteTemplates();
      const templates = res.data;
      const defaultTemplate = templates.find((t) => t.is_default === 1) || templates[0];
      if (defaultTemplate) {
        setTemplateId(defaultTemplate.id);
        const parsed = typeof defaultTemplate.template_config === 'string'
          ? JSON.parse(defaultTemplate.template_config)
          : defaultTemplate.template_config;
        // Merge with defaults to fill any missing fields
        setConfig({
          ...DEFAULT_TEMPLATE_CONFIG,
          ...parsed,
          colors: { ...DEFAULT_TEMPLATE_CONFIG.colors, ...(parsed.colors || {}) },
          sections: { ...DEFAULT_TEMPLATE_CONFIG.sections, ...(parsed.sections || {}) },
          spacing: { ...DEFAULT_TEMPLATE_CONFIG.spacing, ...(parsed.spacing || {}) },
          customSections: parsed.customSections || DEFAULT_TEMPLATE_CONFIG.customSections,
          sectionOrder: parsed.sectionOrder || DEFAULT_TEMPLATE_CONFIG.sectionOrder,
        });
      }
    } catch (err) {
      console.error('Failed to load template:', err);
      setMessage({ type: 'error', text: 'Failed to load template. Using defaults.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!templateId) {
      setMessage({ type: 'error', text: 'No template found to update.' });
      return;
    }
    try {
      setSaving(true);
      await updateQuoteTemplate(templateId, { template_config: config });
      setMessage({ type: 'success', text: 'Template saved successfully.' });
    } catch (err) {
      console.error('Failed to save template:', err);
      setMessage({ type: 'error', text: 'Failed to save template. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setConfig(DEFAULT_TEMPLATE_CONFIG);
    setMessage({ type: 'success', text: 'Template reset to defaults. Click Save to persist.' });
  };

  // Updater helpers
  const updateColor = (key, value) => setConfig((prev) => ({ ...prev, colors: { ...prev.colors, [key]: value } }));
  const updateSection = (key, value) => setConfig((prev) => ({ ...prev, sections: { ...prev.sections, [key]: value } }));
  const updateSpacing = (key, value) => setConfig((prev) => ({ ...prev, spacing: { ...prev.spacing, [key]: value } }));
  const updateField = (key, value) => setConfig((prev) => ({ ...prev, [key]: value }));

  // Custom section helpers
  const addCustomSection = () => {
    const id = 'cs_' + Date.now();
    const newSection = { id, title: '', content: '', enabled: true };
    setConfig((prev) => ({
      ...prev,
      customSections: [...prev.customSections, newSection],
      sectionOrder: [...prev.sectionOrder, `customSection:${id}`],
    }));
  };

  const removeCustomSection = (id) => {
    setConfig((prev) => ({
      ...prev,
      customSections: prev.customSections.filter((s) => s.id !== id),
      sectionOrder: prev.sectionOrder.filter((e) => e !== `customSection:${id}`),
    }));
  };

  const updateCustomSection = (id, field, value) => {
    setConfig((prev) => ({
      ...prev,
      customSections: prev.customSections.map((s) => s.id === id ? { ...s, [field]: value } : s),
    }));
  };

  // Section order drag-and-drop
  const handleDragStart = (index) => {
    dragItem.current = index;
  };

  const handleDragEnter = (index) => {
    dragOverItem.current = index;
  };

  const handleDragEnd = () => {
    const from = dragItem.current;
    const to = dragOverItem.current;
    dragItem.current = null;
    dragOverItem.current = null;

    if (from === null || to === null || from === to) return;

    setConfig((prev) => {
      const updated = [...prev.sectionOrder];
      const [removed] = updated.splice(from, 1);
      updated.splice(to, 0, removed);
      return { ...prev, sectionOrder: updated };
    });
  };

  // Permission check
  if (!hasPermission('manage_settings')) {
    return (
      <div className="container" style={{ padding: '80px 24px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }}>
          <Eye size={48} />
        </div>
        <h2 style={{ marginBottom: '8px', color: 'var(--text-primary)' }}>Access Denied</h2>
        <p style={{ color: 'var(--text-secondary)' }}>You do not have permission to manage quote templates.</p>
        <button className="btn btn-secondary" style={{ marginTop: '20px' }} onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container" style={{ padding: '80px 24px', textAlign: 'center' }}>
        <div style={{ color: 'var(--text-secondary)', fontSize: '15px' }}>Loading template...</div>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: '24px', paddingBottom: '48px' }}>
      {/* Page header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' }}>Quote PDF Template</h1>
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: 0 }}>Customize how your quote PDFs look</p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {message.text && (
            <div style={{
              padding: '8px 14px',
              borderRadius: 'var(--radius-md)',
              fontSize: '13px',
              fontWeight: 500,
              background: message.type === 'success' ? '#dcfce7' : '#fee2e2',
              color: message.type === 'success' ? '#166534' : '#991b1b',
              animation: 'fadeIn 0.2s ease',
            }}>
              {message.text}
            </div>
          )}
          <button className="btn btn-secondary" onClick={handleReset} disabled={saving}>
            <RotateCcw size={16} />
            Reset to Defaults
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            <Save size={16} />
            {saving ? 'Saving...' : 'Save Template'}
          </button>
        </div>
      </div>

      {/* Two-column layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 0.67fr', gap: '24px', alignItems: 'start' }}>
        {/* Left column — Settings panels */}
        <div>
          {/* Card 1: Brand Colors */}
          <SectionCard icon={Palette} title="Brand Colors">
            <ColorPicker label="Primary Color" value={config.colors.primary} onChange={(v) => updateColor('primary', v)} />
            <ColorPicker label="Accent Color" value={config.colors.accent} onChange={(v) => updateColor('accent', v)} />
            <ColorPicker label="Accent Light" value={config.colors.accentLight} onChange={(v) => updateColor('accentLight', v)} />
            <p className="text-xs text-muted mb-0" style={{ marginTop: '4px' }}>
              Primary is used for headers and footers. Accent for borders and highlights. Accent Light for callout backgrounds.
            </p>
          </SectionCard>

          {/* Card 2: Header & Footer */}
          <SectionCard icon={Type} title="Header & Footer">
            <div className="form-group">
              <label className="form-label">Header Title</label>
              <input
                type="text"
                className="form-input"
                value={config.headerText}
                onChange={(e) => updateField('headerText', e.target.value)}
                placeholder="Quotation"
              />
            </div>
            <Toggle label="Show Tagline" checked={config.showTagline} onChange={(v) => updateField('showTagline', v)} />
            <div className="form-group" style={{ marginTop: '8px' }}>
              <label className="form-label">Footer Text</label>
              <input
                type="text"
                className="form-input"
                value={config.footerText}
                onChange={(e) => updateField('footerText', e.target.value)}
                placeholder="Confidential & Proprietary"
              />
            </div>
            <Toggle label="Show Page Numbers" checked={config.showPageNumbers} onChange={(v) => updateField('showPageNumbers', v)} />
          </SectionCard>

          {/* Card 3: Visible Sections */}
          <SectionCard icon={Eye} title="Visible Sections">
            <Toggle label="Customer Information" checked={config.sections.showCustomerInfo} onChange={(v) => updateSection('showCustomerInfo', v)} />
            <Toggle label="Lead Time Column" checked={config.sections.showLeadTime} onChange={(v) => updateSection('showLeadTime', v)} />
            <Toggle label="Terms & Conditions" checked={config.sections.showTerms} onChange={(v) => updateSection('showTerms', v)} />
            <Toggle label="Special Requirements" checked={config.sections.showSpecialRequirements} onChange={(v) => updateSection('showSpecialRequirements', v)} />
            <Toggle label="Sales Representative" checked={config.sections.showSalesRep} onChange={(v) => updateSection('showSalesRep', v)} />
            <Toggle label="Validity Period in Meta Bar" checked={config.sections.showValidity} onChange={(v) => updateSection('showValidity', v)} />
          </SectionCard>

          {/* Card 4: Spacing */}
          <SectionCard icon={Sliders} title="Spacing">
            <RangeSlider label="Section Spacing" value={config.spacing.sectionSpacing} onChange={(v) => updateSpacing('sectionSpacing', v)} min={6} max={24} step={2} />
            <RangeSlider label="Field Spacing" value={config.spacing.fieldSpacing} onChange={(v) => updateSpacing('fieldSpacing', v)} min={6} max={18} step={2} />
            <Toggle label="Compact Mode" checked={config.spacing.compactMode} onChange={(v) => updateSpacing('compactMode', v)} />
          </SectionCard>

          {/* Card 5: Default Terms */}
          <SectionCard icon={FileText} title="Default Terms">
            <div className="form-group mb-0">
              <label className="form-label">Standard Terms & Conditions</label>
              <textarea
                className="form-textarea"
                value={config.defaultTerms}
                onChange={(e) => updateField('defaultTerms', e.target.value)}
                rows={5}
                placeholder="Enter your default terms and conditions text..."
                style={{ minHeight: '120px' }}
              />
              <p className="text-xs text-muted mb-0" style={{ marginTop: '6px' }}>
                This text appears in the Terms & Conditions callout box on the PDF.
              </p>
            </div>
          </SectionCard>

          {/* Card 6: Custom Sections */}
          <SectionCard
            icon={Plus}
            title="Custom Sections"
            action={
              <button
                className="btn btn-secondary"
                style={{ padding: '4px 10px', fontSize: '12px' }}
                onClick={addCustomSection}
              >
                <Plus size={14} /> Add Section
              </button>
            }
          >
            {config.customSections.length === 0 && (
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', textAlign: 'center', padding: '12px 0', margin: 0 }}>
                No custom sections yet. Click "Add Section" to create one.
              </p>
            )}
            {config.customSections.map((cs) => (
              <div
                key={cs.id}
                style={{
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-md)',
                  padding: '12px',
                  marginBottom: '10px',
                  background: cs.enabled ? 'var(--bg-primary)' : 'var(--bg-tertiary)',
                  opacity: cs.enabled ? 1 : 0.6,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <input
                    type="text"
                    className="form-input"
                    value={cs.title}
                    onChange={(e) => updateCustomSection(cs.id, 'title', e.target.value)}
                    placeholder="Section title..."
                    style={{ flex: 1, fontSize: '13px', fontWeight: 600 }}
                  />
                  <div
                    onClick={() => updateCustomSection(cs.id, 'enabled', !cs.enabled)}
                    title={cs.enabled ? 'Disable section' : 'Enable section'}
                    style={{
                      width: '32px', height: '20px', borderRadius: '10px',
                      background: cs.enabled ? 'var(--accent-primary)' : 'var(--border-color)',
                      position: 'relative', cursor: 'pointer', flexShrink: 0,
                    }}
                  >
                    <div style={{
                      width: '16px', height: '16px', borderRadius: '50%', background: '#fff',
                      position: 'absolute', top: '2px', left: cs.enabled ? '14px' : '2px',
                      transition: 'left 0.2s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                    }} />
                  </div>
                  <button
                    onClick={() => removeCustomSection(cs.id)}
                    title="Remove section"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: '4px', borderRadius: '4px' }}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
                <textarea
                  className="form-textarea"
                  value={cs.content}
                  onChange={(e) => updateCustomSection(cs.id, 'content', e.target.value)}
                  placeholder="Section content..."
                  rows={3}
                  style={{ fontSize: '13px', minHeight: '60px' }}
                />
              </div>
            ))}
          </SectionCard>

          {/* Card 7: Section Order */}
          <SectionCard icon={Layers} title="Section Order">
            <p className="text-xs text-muted" style={{ marginBottom: '10px', marginTop: 0 }}>
              Drag to reorder sections that appear after the line items table in the PDF.
            </p>
            {config.sectionOrder.map((entry, index) => (
              <div
                key={entry}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragEnter={() => handleDragEnter(index)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => e.preventDefault()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 10px',
                  marginBottom: '4px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'grab',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: 'var(--text-primary)',
                }}
              >
                <GripVertical size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                <span style={{ flex: 1 }}>{getSectionLabel(entry, config.customSections)}</span>
                {entry.startsWith('customSection:') && (
                  <span style={{ fontSize: '10px', color: 'var(--text-muted)', background: 'var(--bg-tertiary)', padding: '1px 6px', borderRadius: '3px' }}>Custom</span>
                )}
              </div>
            ))}
            {config.sectionOrder.length === 0 && (
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', textAlign: 'center', padding: '8px 0', margin: 0 }}>
                No sections to order.
              </p>
            )}
          </SectionCard>
        </div>

        {/* Right column — Live Preview */}
        <div>
          <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Layout size={16} style={{ color: 'var(--text-secondary)' }} />
            <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Live Preview</span>
          </div>
          <LivePreview config={config} />
          <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '10px', textAlign: 'center' }}>
            This is a simplified preview. The actual PDF may vary slightly.
          </p>
        </div>
      </div>
    </div>
  );
}

export default QuoteTemplateEditor;
